import {
  MessageStatus,
  QueryKeys,
  addOptimisticMessage,
  confirmOptimisticMessage,
  type MessageWithStatus,
} from '@sunrise-ui/api-client';
import { type Room } from '@sunrise-ui/api/events';
import {
  type ArcadeAuctionsOutputsGetRoundID,
  type ArcadeRoomExtensionsOutputsGetRoundID,
  type GetCommitmentTotalOutput,
  type GetExtensionPriceOutput,
  type HighestBid,
} from '@sunrise-ui/api/gamification';
import { useAuthStore, useSafeContext } from '@sunrise-ui/primitives';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, {
  createContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from 'react';
import {
  initialiseNotificationService,
  type NotificationService,
} from '../services/NotificationService';

const { inboxKey } = QueryKeys.inbox;
const {
  messagesKey,
  messagesPageSize,
  conversationsKey,
  unreadCountKey,
  unreadKey,
} = QueryKeys.conversations;
const { eventMessagesKey, eventMessagesPageSize } =
  QueryKeys.eventConversations;
const { roomsKey, byEventId } = QueryKeys.rooms;
const { auctionsKey, highestBid, currentAuctionRound } = QueryKeys.auctions;
const { extensionsKey, price, commitments, currentExtensionRound } =
  QueryKeys.extensions;

export type NotificationServiceContextType = {
  notificationService?: NotificationService;
};

const NotificationServiceContext =
  // @ts-expect-error -- don't use default, instead console error if component not used in appropriate context
  createContext<NotificationServiceContextType>();
NotificationServiceContext.displayName = 'NotificationServiceContext';

export const NotificationServiceProvider = ({
  children,
}: PropsWithChildren) => {
  const { isAuthenticated, currentUserId, authToken } = useAuthStore();
  const queryClient = useQueryClient();
  const [notificationService, setNotificationService] =
    useState<NotificationService>();

  useEffect(() => {
    const initNotificationService = initialiseNotificationService();

    setNotificationService(initNotificationService);
  }, []);

  useEffect(() => {
    if (notificationService && isAuthenticated && authToken) {
      void notificationService.openSocket(authToken);
    }
    return () => {
      notificationService?.closeSocket();
    };
  }, [notificationService, authToken, isAuthenticated]);

  useEffect(() => {
    if (notificationService && isAuthenticated && currentUserId) {
      const removeMessageSentListener = notificationService.addListener(
        'MessageSent',
        async (message) => {
          const conversationId = message.conversation_id;
          const isFromCurrentUser = message.user_id === currentUserId;

          await queryClient.invalidateQueries({ queryKey: [inboxKey] });

          if (isFromCurrentUser) {
            // Only confirm optimistic message if it exists
            const messagesQueryKey = [
              messagesKey,
              conversationId,
              messagesPageSize,
            ];
            const eventMessagesQueryKey = [
              eventMessagesKey,
              conversationId,
              eventMessagesPageSize,
            ];

            // Check if there's a pending message to confirm
            const currentData = queryClient.getQueryData(messagesQueryKey) as
              | {
                  pages: Array<{ results: MessageWithStatus[] }>;
                }
              | undefined;

            const hasPendingMessage = currentData?.pages?.[0]?.results?.some(
              (msg: MessageWithStatus) =>
                msg.content === message.content &&
                msg._id?.toString().startsWith('temp-'),
            );

            if (hasPendingMessage) {
              // Confirm existing optimistic message
              confirmOptimisticMessage(
                message.content,
                message,
                messagesQueryKey,
                queryClient,
              );
              confirmOptimisticMessage(
                message.content,
                message,
                eventMessagesQueryKey,
                queryClient,
              );
            }
          } else {
            // Message from someone else, add as new message
            const messageWithStatus: MessageWithStatus = {
              ...message,
              status: MessageStatus.CONFIRMED,
            };
            addOptimisticMessage(
              messageWithStatus,
              [messagesKey, conversationId, messagesPageSize],
              queryClient,
            );
            addOptimisticMessage(
              messageWithStatus,
              [eventMessagesKey, conversationId, eventMessagesPageSize],
              queryClient,
            );
          }

          // Invalidate after updating cache so that any new query mounted will refetch
          await queryClient.invalidateQueries({
            queryKey: [messagesKey, conversationId, messagesPageSize],
            refetchType: 'none',
          });
          await queryClient.invalidateQueries({
            queryKey: [conversationsKey, currentUserId, unreadCountKey],
          });
          await queryClient.invalidateQueries({
            queryKey: [conversationsKey, conversationId, unreadKey],
          });
        },
      );

      const removeNewExtensionRoundInitiatedListener =
        notificationService.addListener('ExtensionAchieved', async (event) => {
          queryClient.setQueryData<Room>([roomsKey, event.room_id], (room) => {
            if (room) {
              const newEndDate = DateTime.fromISO(
                room.scheduled_end_date ?? DateTime.now().toISO(),
              ).plus({
                second: Number(event.extend_by_sec),
              });

              return {
                ...room,
                scheduled_end_date: newEndDate.toISO() ?? '',
              };
            }
          });
          queryClient.setQueryData<ArcadeRoomExtensionsOutputsGetRoundID>(
            [extensionsKey, currentExtensionRound, event.room_id],
            () => undefined,
          );
          const priceData = queryClient.getQueryData<GetExtensionPriceOutput>([
            extensionsKey,
            price,
            event.room_id,
          ]);
          queryClient.setQueryData<GetCommitmentTotalOutput>(
            [extensionsKey, commitments, event.room_id],
            () => ({ total: priceData?.price ?? 0 }),
          );
        });

      const removeNewExtensionRoundCreatedListener =
        notificationService.addListener(
          'NewExtensionRoundCreated',
          async (event) => {
            queryClient.setQueryData<GetExtensionPriceOutput>(
              [extensionsKey, price, event.room_id],
              () => ({ price: event.price }),
            );
            queryClient.setQueryData<GetCommitmentTotalOutput>(
              [extensionsKey, commitments, event.room_id],
              () => ({ total: 0 }),
            );
            queryClient.setQueryData<ArcadeRoomExtensionsOutputsGetRoundID>(
              [extensionsKey, currentExtensionRound, event.room_id],
              () => ({ round_id: event.round_id }),
            );
          },
        );

      const removeExtensionCommitmentCreatedListener =
        notificationService.addListener(
          'ExtensionCommitmentCreated',
          async (event) => {
            const round =
              queryClient.getQueryData<ArcadeRoomExtensionsOutputsGetRoundID>([
                extensionsKey,
                currentExtensionRound,
                event.room_id,
              ]);
            // Guard using the round as this event could occur after the ExtensionAchieved one
            if (round) {
              queryClient.setQueryData<GetCommitmentTotalOutput>(
                [extensionsKey, commitments, event.room_id],
                () => ({ total: event.total }),
              );
            }
          },
        );

      const removeHostJoinedVideoRoomListener = notificationService.addListener(
        'HostJoinedVideoRoom',
        async (event) => {
          const room = queryClient.getQueryData<Room>([
            roomsKey,
            event.room_id,
          ]);
          const liveRoom = { ...room, is_live: true };
          await queryClient.setQueryData(
            [roomsKey, event.room_id],
            () => liveRoom,
          );
          await queryClient.setQueryData(
            [roomsKey, byEventId, event.event_id],
            () => liveRoom,
          );
        },
      );

      const removeNewAuctionRoundCreatedListener =
        notificationService.addListener(
          'NewAuctionRoundCreated',
          async (event) => {
            queryClient.setQueryData<HighestBid>(
              [auctionsKey, highestBid, event.room_id],
              () => ({
                room_id: event.room_id,
                round_id: event.round_id,
                amount: event.price,
              }),
            );

            queryClient.setQueryData<ArcadeAuctionsOutputsGetRoundID>(
              [auctionsKey, currentAuctionRound, event.room_id],
              () => ({
                round_id: event.round_id,
              }),
            );
          },
        );

      const removeNewHighestBidPlacedListener = notificationService.addListener(
        'NewHighestBidPlaced',
        async (event) => {
          queryClient.setQueryData<HighestBid>(
            [auctionsKey, highestBid, event.room_id],
            () => ({
              room_id: event.room_id,
              round_id: event.round_id,
              amount: event.amount,
              user_id: event.user_id,
            }),
          );
        },
      );

      const removeAuctionRoundClosedListener = notificationService.addListener(
        'AuctionRoundClosed',
        async (event) => {
          queryClient.setQueryData<ArcadeAuctionsOutputsGetRoundID>(
            [auctionsKey, currentAuctionRound, event.room_id],
            () => undefined,
          );
        },
      );

      return () => {
        removeMessageSentListener();
        removeNewExtensionRoundCreatedListener();
        removeNewExtensionRoundInitiatedListener();
        removeExtensionCommitmentCreatedListener();
        removeHostJoinedVideoRoomListener();
        removeAuctionRoundClosedListener();
        removeNewHighestBidPlacedListener();
        removeNewAuctionRoundCreatedListener();
      };
    }
  }, [notificationService, currentUserId, queryClient, isAuthenticated]);

  return (
    <NotificationServiceContext.Provider
      value={{
        notificationService,
      }}
    >
      {children}
    </NotificationServiceContext.Provider>
  );
};

export default function useNotificationService() {
  return useSafeContext(NotificationServiceContext);
}
